'use client'

import { type FC } from 'react'

import { AdServerExperimentActivation } from '../ad-server'
import { AppsFlyerBanner } from '../apps-flyer-banner'
import { CartIntermediateContextProvider } from '../cart-intermediate'
import { ErrorBoundary } from '../error-boundary'
import { ExperimentsPageTypeProvider } from '../experiments/components/ExperimentsPageTypeContext'
import { EXPERIMENT_PAGE_TYPE_PRODUCT } from '../experiments/ExperimentPageType'
import { useSetSearchSessionStorageValuesForOptimizelyTracking } from '../experiments/hooks'
import { FirstImpressionProvider } from '../first-impression'
import { NowGeneralContextProvider } from '../now'
import {
  RecommendationConfigProvider,
  RecommendationInactivityModalDynamic,
} from '../recommendation'
import { SellerModalProvider } from '../seller'
import { StandardShopLayout } from '../standard-shop-layout'

import { ProductBreadcrumbs } from './components/ProductBreadcrumbs/ProductBreadcrumbs'
import { ProductPageAdServerPushCommand } from './components/ProductPageAdServerPushCommand'
import { ProductPageContextProvider } from './components/productPageContext'
import { ProductPageOverview } from './components/ProductPageCswPosition'
import { ProductPageJsonLdProductDescription } from './components/ProductPageJsonLdProductDescription/ProductPageJsonLdProductDescription'
import { DynamicProductPageLatestActivityRecommendation } from './components/ProductPageLatestActivity'
import { type ProductPageProps } from './components/ProductPageProps'
import { ProductOrderLimitContextProvider } from './product-order-limit/components/productOrderLimitContext'
import { ProductPageSeoHeadTags } from './seo/components/ProductPageSeoHeadTags/ProductPageSeoHeadTags'

export const ProductPageContent: FC<ProductPageProps> = ({
  breadcrumbs,
  firstImpression,
  productPageConfig,
  productPageData,
  recommendationConfig,
  standardLayoutData,
}) => {
  useSetSearchSessionStorageValuesForOptimizelyTracking()

  /* eslint-disable react/jsx-max-depth -- the rule was disabled before a comment became required */
  return (
    <ExperimentsPageTypeProvider pageType={EXPERIMENT_PAGE_TYPE_PRODUCT}>
      <StandardShopLayout standardLayoutData={standardLayoutData}>
        <main>
          <AppsFlyerBanner config={productPageData.appsFlyerBannerConfig} />
          <NowGeneralContextProvider>
            <FirstImpressionProvider firstImpression={firstImpression}>
              <ProductOrderLimitContextProvider
                config={productPageData.productOrderLimitConfig}
              >
                <ProductPageContextProvider
                  breadcrumbs={breadcrumbs}
                  channel={productPageData.channel}
                  faqs={productPageData.faqs}
                  followUpProduct={productPageData.followUpProduct}
                  pageParams={productPageData.pageParams}
                  product={productPageData.product}
                  productPageConfig={productPageConfig}
                  productPageOdsConfig={productPageData.productPageOdsConfig}
                  promotionProduct={productPageData.promotionProduct}
                  reviewData={productPageData.reviewData}
                  shipping={productPageData.shipping}
                  socialMediaConfig={productPageData.socialMediaConfig}
                >
                  <AdServerExperimentActivation />
                  <ProductPageSeoHeadTags />
                  <RecommendationConfigProvider value={recommendationConfig}>
                    <SellerModalProvider>
                      <CartIntermediateContextProvider
                        pdpGtmData={productPageData.gtmDataProps}
                      >
                        <ProductPageAdServerPushCommand />
                        <ProductPageJsonLdProductDescription />
                        <ProductBreadcrumbs />
                        <div
                          className="container pt-5"
                          data-qa-id="product-details-page"
                        >
                          <ProductPageOverview />
                          <ErrorBoundary>
                            <DynamicProductPageLatestActivityRecommendation />
                          </ErrorBoundary>
                        </div>
                        <RecommendationInactivityModalDynamic />
                      </CartIntermediateContextProvider>
                    </SellerModalProvider>
                  </RecommendationConfigProvider>
                </ProductPageContextProvider>
              </ProductOrderLimitContextProvider>
            </FirstImpressionProvider>
          </NowGeneralContextProvider>
        </main>
      </StandardShopLayout>
    </ExperimentsPageTypeProvider>
  )
  /* eslint-enable react/jsx-max-depth */
}
